import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import {
  Button,
  Card,
  Col,
  Form as BsForm,
  Row,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';
import moment from 'moment';
import { toast } from 'react-toastify';

import * as constants from '~/constant';
import {
  customStyles,
  DropdownIndicator,
  selectStylesSm,
} from '~/Styles/reactSelectStyles';
import {
  listarGruposEmpresaFechamentoCaixa,
  listarContasFechamentoCaixa,
  listarMoedasFechamentoCaixa,
  obterProcessoAnteriorFechamentoCaixa,
} from '~/Services/Service';

import { filtrosSchema } from '../validationSchema';

const extrairMensagemErro = (err, fallback) => {
  if (err && typeof err === 'object' && err.message) return err.message;
  return fallback;
};

const formatarDataExibicao = (valorIso) => {
  if (!valorIso) return '';
  const d = moment(valorIso);
  return d.isValid() ? d.format('DD/MM/YYYY') : '';
};

const SecaoFiltros = ({ onBuscar, buscando, disabled, valoresIniciais }) => {
  const [grupos, setGrupos] = useState([]);
  const [contas, setContas] = useState([]);
  const [moedas, setMoedas] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingContas, setLoadingContas] = useState(false);
  const [loadingMoedas, setLoadingMoedas] = useState(false);
  const [loadingProcesso, setLoadingProcesso] = useState(false);
  const [statusProcesso, setStatusProcesso] = useState(null);

  useEffect(() => {
    let ativo = true;
    setLoadingGrupos(true);
    setLoadingMoedas(true);
    Promise.all([
      listarGruposEmpresaFechamentoCaixa(),
      listarMoedasFechamentoCaixa(),
    ])
      .then(([gruposRes, moedasRes]) => {
        if (!ativo) return;
        setGrupos(Array.isArray(gruposRes) ? gruposRes : []);
        setMoedas(Array.isArray(moedasRes) ? moedasRes : []);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(
          extrairMensagemErro(err, 'Erro ao carregar Grupos de Empresa e Moedas')
        );
      })
      .finally(() => {
        if (!ativo) return;
        setLoadingGrupos(false);
        setLoadingMoedas(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const carregarContas = (ge, callback) => {
    let ativo = true;
    setLoadingContas(true);
    setContas([]);
    listarContasFechamentoCaixa(ge)
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setContas(lista);
        if (callback) callback(lista);
      })
      .catch((err) => {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err, 'Erro ao carregar Contas de Caixa'));
      })
      .finally(() => {
        if (!ativo) return;
        setLoadingContas(false);
      });
    return () => {
      ativo = false;
    };
  };

  const consultarProcessoAnterior = (ge, conta, ativo, setFieldValue) => {
    if (!ge || !conta || !ativo) return;
    let alive = true;
    setLoadingProcesso(true);
    setStatusProcesso(null);
    obterProcessoAnteriorFechamentoCaixa(ge, conta, ativo)
      .then((res) => {
        if (!alive) return;
        const status = res && res.status ? res.status : null;
        setStatusProcesso(status);

        if (status === 'I' && res.data_inicial) {
          setFieldValue('dt_inicial', formatarDataExibicao(res.data_inicial));
        } else if (status === 'F' && res.data_mata_anterior) {
          setFieldValue(
            'dt_inicial',
            formatarDataExibicao(res.data_mata_anterior)
          );
        } else {
          setFieldValue('dt_inicial', '');
        }
      })
      .catch((err) => {
        if (!alive) return;
        setStatusProcesso(null);
        toast.error(
          extrairMensagemErro(err, 'Erro ao consultar processo anterior')
        );
      })
      .finally(() => {
        if (!alive) return;
        setLoadingProcesso(false);
      });
    return () => {
      alive = false;
    };
  };

  const grupoOptions = grupos.map((g) => ({
    value: g.codigo_grupo_empresa,
    label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
  }));
  const contaOptions = contas.map((c) => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta}`,
    contaDiferenca: c.conta_diferenca,
  }));
  const moedaOptions = moedas.map((m) => ({
    value: m.codigo_ativo,
    label: `${m.codigo_ativo} - ${m.nome}`,
  }));

  const dtInicialDisabled = statusProcesso === 'I' || statusProcesso === 'F';

  const handleSubmit = (values, { setSubmitting }) => {
    const contaSelecionada = contas.find(
      (c) => c.codigo_conta === values.codigo_conta
    );
    const dtIso = moment(values.dt_inicial, 'DD/MM/YYYY').format('YYYY-MM-DD');
    onBuscar(
      {
        codigo_grupo_empresa: values.codigo_grupo_empresa,
        codigo_conta: values.codigo_conta,
        codigo_ativo: values.codigo_ativo,
        dt_inicial: dtIso,
        conta_diferenca: contaSelecionada ? contaSelecionada.conta_diferenca : null,
      },
      statusProcesso
    );
    setSubmitting(false);
  };

  return (
    <Card className="mb-3">
      <Card.Header>
        <strong>Filtros</strong>
      </Card.Header>
      <Card.Body>
        <Formik
          initialValues={
            valoresIniciais || {
              codigo_grupo_empresa: '',
              codigo_conta: '',
              codigo_ativo: '',
              dt_inicial: '',
            }
          }
          validationSchema={filtrosSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <Row>
                <Col xs={12} md={6} lg={4} className="mb-2">
                  <BsForm.Group controlId="ge">
                    <small>Grupo de Empresa</small>
                    <Select
                      placeholder={
                        loadingGrupos ? 'Carregando...' : 'Selecione o GE'
                      }
                      isLoading={loadingGrupos}
                      isDisabled={disabled || loadingGrupos}
                      options={grupoOptions}
                      value={
                        grupoOptions.find(
                          (o) => o.value === values.codigo_grupo_empresa
                        ) || null
                      }
                      onChange={(opt) => {
                        const novoGe = opt ? opt.value : '';
                        setFieldValue('codigo_grupo_empresa', novoGe);
                        setFieldValue('codigo_conta', '');
                        setFieldValue('dt_inicial', '');
                        setStatusProcesso(null);
                        setContas([]);
                        if (novoGe) carregarContas(novoGe);
                      }}
                      styles={{ ...customStyles, ...selectStylesSm }}
                      components={{ DropdownIndicator }}
                      isClearable
                    />
                    {errors.codigo_grupo_empresa &&
                      touched.codigo_grupo_empresa && (
                        <div className="text-danger small mt-1">
                          {errors.codigo_grupo_empresa}
                        </div>
                      )}
                  </BsForm.Group>
                </Col>

                <Col xs={12} md={6} lg={4} className="mb-2">
                  <BsForm.Group controlId="conta">
                    <small>Conta de Caixa</small>
                    <Select
                      placeholder={
                        loadingContas ? 'Carregando...' : 'Selecione a Conta'
                      }
                      isLoading={loadingContas}
                      isDisabled={
                        disabled ||
                        !values.codigo_grupo_empresa ||
                        loadingContas
                      }
                      options={contaOptions}
                      value={
                        contaOptions.find(
                          (o) => o.value === values.codigo_conta
                        ) || null
                      }
                      onChange={(opt) => {
                        const novaConta = opt ? opt.value : '';
                        setFieldValue('codigo_conta', novaConta);
                        setFieldValue('dt_inicial', '');
                        setStatusProcesso(null);
                        if (novaConta && values.codigo_ativo) {
                          consultarProcessoAnterior(
                            values.codigo_grupo_empresa,
                            novaConta,
                            values.codigo_ativo,
                            setFieldValue
                          );
                        }
                      }}
                      styles={{ ...customStyles, ...selectStylesSm }}
                      components={{ DropdownIndicator }}
                      isClearable
                    />
                    {errors.codigo_conta && touched.codigo_conta && (
                      <div className="text-danger small mt-1">
                        {errors.codigo_conta}
                      </div>
                    )}
                  </BsForm.Group>
                </Col>

                <Col xs={12} md={6} lg={4} className="mb-2">
                  <BsForm.Group controlId="moeda">
                    <small>Moeda</small>
                    <Select
                      placeholder={
                        loadingMoedas ? 'Carregando...' : 'Selecione a Moeda'
                      }
                      isLoading={loadingMoedas}
                      isDisabled={disabled || loadingMoedas}
                      options={moedaOptions}
                      value={
                        moedaOptions.find(
                          (o) => o.value === values.codigo_ativo
                        ) || null
                      }
                      onChange={(opt) => {
                        const novoAtivo = opt ? opt.value : '';
                        setFieldValue('codigo_ativo', novoAtivo);
                        setFieldValue('dt_inicial', '');
                        setStatusProcesso(null);
                        if (
                          novoAtivo &&
                          values.codigo_grupo_empresa &&
                          values.codigo_conta
                        ) {
                          consultarProcessoAnterior(
                            values.codigo_grupo_empresa,
                            values.codigo_conta,
                            novoAtivo,
                            setFieldValue
                          );
                        }
                      }}
                      styles={{ ...customStyles, ...selectStylesSm }}
                      components={{ DropdownIndicator }}
                      isClearable
                    />
                    {errors.codigo_ativo && touched.codigo_ativo && (
                      <div className="text-danger small mt-1">
                        {errors.codigo_ativo}
                      </div>
                    )}
                  </BsForm.Group>
                </Col>

                <Col xs={12} md={6} lg={4} className="mb-2">
                  <BsForm.Group controlId="dtInicial">
                    <small>
                      Data Inicial
                      {statusProcesso === 'I' && (
                        <span className="text-muted ms-1">
                          (processo em andamento)
                        </span>
                      )}
                      {statusProcesso === 'F' && (
                        <span className="text-muted ms-1">
                          (herdada do último fechamento)
                        </span>
                      )}
                    </small>
                    <MaskedInput
                      name="dt_inicial"
                      type="tel"
                      guide={false}
                      className={`form-control form-control-sm ${
                        errors.dt_inicial && touched.dt_inicial
                          ? 'is-invalid'
                          : ''
                      }`}
                      placeholder="Ex.: 01/01/2026"
                      mask={constants.MASK_DATE}
                      value={values.dt_inicial}
                      onChange={(e) =>
                        setFieldValue('dt_inicial', e.target.value)
                      }
                      disabled={disabled || dtInicialDisabled || loadingProcesso}
                    />
                    {errors.dt_inicial && touched.dt_inicial && (
                      <div className="text-danger small mt-1">
                        {errors.dt_inicial}
                      </div>
                    )}
                  </BsForm.Group>
                </Col>

                <Col xs={12} className="d-flex align-items-end mb-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={disabled || isSubmitting || buscando}
                  >
                    {buscando ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-1"
                        />
                        Buscando...
                      </>
                    ) : (
                      'Buscar'
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default SecaoFiltros;
